import PropTypes from 'prop-types';
import React from 'react';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { formatDuration, formatNumber, formatPercent, ratio } from './format';
import { reportShort } from './reports';

/**
 * Üstteki altı kutu. Her kutunun tepesinde rakamın hangi rapordan geldiği
 * yazıyor — mutabakat toplantısında ilk sorulan şey bu olduğu için kaynak
 * ekranda duruyor.
 *
 * Kutular ayrı kart değil, tek bir çerçevenin içinde ince çizgilerle bölünmüş
 * bir şerit; altısı da aynı ölçünün parçaları olduğu için birlikte okunuyor.
 */
const buildTiles = (summary) => {
  if (!summary) return [];
  return [
    {
      source: reportShort(14),
      label: 'Santrale gelen',
      value: formatNumber(summary.switchboardCalls),
      hint: 'operatör kanalından'
    },
    {
      source: reportShort(1),
      label: 'Kuyruğa düşen',
      value: formatNumber(summary.queuedCalls),
      hint: `${formatPercent(ratio(summary.queuedCalls, summary.switchboardCalls))} · ${formatNumber(summary.queueCount)} kuyruk`
    },
    {
      source: reportShort(1),
      label: 'Cevaplanan',
      value: formatNumber(summary.answered),
      hint: `${formatPercent(summary.answerRate)} cevaplama`
    },
    {
      source: reportShort(1),
      label: 'Kaçan',
      value: formatNumber(summary.abandoned),
      hint: `${formatPercent(ratio(summary.abandoned, summary.queuedCalls))} kayıp`
    },
    {
      source: reportShort(1),
      label: 'Servis seviyesi 80/20',
      value: formatPercent(summary.serviceLevel),
      hint: 'hacim ağırlıklı'
    },
    {
      source: reportShort(6),
      label: 'Ortalama AHT',
      value: formatDuration(summary.aht),
      unit: ' dk',
      hint: `${formatNumber(summary.agentCount)} agent`
    }
  ];
};

const KpiStrip = ({ summary, loading }) => {
  const tiles = loading ? new Array(6).fill(null) : buildTiles(summary);

  return (
    <Card
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(3,1fr)', xl: 'repeat(6,1fr)' },
        overflow: 'hidden',
        mb: 2
      }}
    >
      {tiles.map((tile, i) => (
        <Box
          key={tile ? tile.label : i}
          sx={(theme) => ({
            px: 2,
            py: 1.75,
            borderLeft: `1px solid ${theme.palette.divider}`,
            borderTop: `1px solid ${theme.palette.divider}`,
            '&:first-of-type': { borderLeft: 0 },
            [theme.breakpoints.up('xl')]: { borderTop: 0 },
            [theme.breakpoints.between('md', 'xl')]: {
              '&:nth-of-type(3n+1)': { borderLeft: 0 },
              '&:nth-of-type(-n+3)': { borderTop: 0 }
            },
            [theme.breakpoints.down('md')]: {
              '&:nth-of-type(odd)': { borderLeft: 0 },
              '&:nth-of-type(-n+2)': { borderTop: 0 }
            }
          })}
        >
          {!tile ? (
            <>
              <Skeleton width="45%" height={12} />
              <Skeleton width="70%" height={16} sx={{ mt: 0.5 }} />
              <Skeleton width="55%" height={32} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: 'text.secondary'
                }}
              >
                {tile.source}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                {tile.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 500,
                  letterSpacing: '-0.6px',
                  lineHeight: 1.15,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {tile.value}
                {tile.unit && (
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                  >
                    {tile.unit}
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                {tile.hint}
              </Typography>
            </>
          )}
        </Box>
      ))}
    </Card>
  );
};

KpiStrip.propTypes = {
  summary: PropTypes.object,
  loading: PropTypes.bool
};

export default KpiStrip;
